import './ServiceSelector.scss';
import { MouseEvent, useCallback, useState } from 'react';
import { getCapitalizeWord, parseDurationToUIString, useGetFacilities, useTranslation } from '../../utils';
import { AbsoluteRoutes } from '../../utils/enums';
import { NextButton } from '../NextButton';
import type { TFacilities } from '../../utils/types';

function formatDuration(raw: string): string {
  const [hStr, mStr] = raw.split(':');
  const hours = parseInt(hStr, 10) || 0;
  const minutes = parseInt(mStr, 10) || 0;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} год`);
  if (minutes > 0) parts.push(`${minutes} хв`);

  return parts.length > 0 ? parts.join(' ') : raw;
}

const ServiceSelector = () => {
  const [selectedService, setSelectedService] = useState<TFacilities>();
  const { t } = useTranslation();
  const { facilities } = useGetFacilities();

  const fakeFacilities = [
    {
      id: 1,
      facility: 'Test consultation',
      duration: '60',
      providedRemotely: false,
      expertFacilityEnable: true,
      geolocation: { fullAddress: 'Test address 1', locale: 'en', lat: 0, lon: 0 },
    },
    {
      id: 2,
      facility: 'Online session',
      duration: '30',
      providedRemotely: true,
      expertFacilityEnable: true,
      geolocation: { fullAddress: 'Zoom / Google Meet', locale: 'en', lat: 0, lon: 0 },
    },
  ] as unknown as TFacilities[];

  const facilitiesToRender = facilities && facilities.length > 0 ? facilities : fakeFacilities;

  const handleSelectService = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const id = e.currentTarget.getAttribute('data-service-id');
      if (id) {
        const selectedFacility = facilitiesToRender.find(item => item.id === Number(id));
        setSelectedService(selectedFacility);
      }
    },
    [facilitiesToRender],
  );

  const renderFacilityName = (val: any): string => {
    if (val && typeof val === 'object') return val.name || JSON.stringify(val);
    return val;
  };

  return (
    <div className='serviceSelector'>
      <h3 className='sectionTitle'>
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <path d='M4 4h16v16H4z' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M4 9h16' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
        {t('Web.Select_expert_service_web')}
      </h3>

      <div className='serviceList'>
        {facilitiesToRender.map((service, i) => {
          const selected = selectedService?.id === service.id;

          return (
            <div
              className={`service ${selected ? 'selected' : ''}`}
              data-service-id={service.id}
              key={i}
              onClick={handleSelectService}
            >
              <div className='service-header'>
                <span className='title'>{renderFacilityName(service.facility)}</span>
                <span className='duration'>
                  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <circle cx='12' cy='12' r='9' />
                    <path d='M12 7v5l3 3' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  {formatDuration(parseDurationToUIString(String(service.duration)))}
                </span>
                {selected && (
                  <span className='checkmark'>
                    <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                      <path d='M20 6L9 17l-5-5' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                  </span>
                )}
              </div>
              {service.geolocation?.fullAddress && (
                <span className='address'>
                  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <path d='M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11z' strokeLinecap='round' strokeLinejoin='round' />
                    <circle cx='12' cy='10' r='2.5' />
                  </svg>
                  {service.geolocation.fullAddress}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <NextButton
        link={AbsoluteRoutes.booking}
        navState={{ facility: selectedService }}
        btnText={getCapitalizeWord(t('Select role.Button_continue'))}
        isDisable={!selectedService}
      />
    </div>
  );
};

export default ServiceSelector;